import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Panel from "../../components/ui/Panel/Panel.jsx";
import Button from "../../components/ui/Button/Button.jsx";
import Tag from "../../components/ui/Tag/Tag.jsx";
import SeoHead from "../../seo/SeoHead.jsx";
import { breadcrumbSchema } from "../../seo/schema.js";
import { getRejectCode, REJECT_CODES } from "../../data/rejectCodes.js";
import { REJECT_SECTIONS } from "./rejectSections.js";

/**
 * One public page per filer-fixable IRS reject code. Everything on it comes
 * from rejectCodes.js (generated from the IRS Business Rules CSV + the API's
 * rejection registry): the IRS's own rule text verbatim, our plain-language
 * explanation, and the fix as steps in this app. Codes only support can fix
 * have no page - they're listed on the hub.
 */
const RejectCodePage = () => {
  const { codeSlug } = useParams();
  const rule = getRejectCode(codeSlug);
  // Unknown or support-only code: no soft-404, send them to the full list.
  if (!rule || !rule.fixable) return <Navigate to="/form-2290-reject-codes" replace />;

  const section = REJECT_SECTIONS[rule.section];
  const siblings = REJECT_CODES.filter((r) => r.fixable && r.section === rule.section && r.code !== rule.code);
  const path = `/form-2290-reject-codes/${rule.codeSlug}`;
  const description = `${rule.explanation} What IRS reject code ${rule.code} means on Form 2290, and how to fix it and file again.`.slice(0, 158);

  return (
    <div className="max-w-[820px] mx-auto px-[22px] py-[46px] pb-[70px]">
      <SeoHead
        title={`IRS Reject Code ${rule.code}: ${rule.title} | EasyFile2290`}
        description={description}
        path={path}
        keywords={[`${rule.code}`, `form 2290 reject code ${rule.code}`, `2290 rejected ${rule.code}`, "form 2290 rejected"]}
        schemas={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Form 2290 reject codes", path: "/form-2290-reject-codes" },
            { name: rule.code, path },
          ]),
        ]}
      />
      <Link to="/form-2290-reject-codes" className="text-[var(--muted)] text-sm no-underline inline-flex items-center gap-1.5 hover:text-[var(--accent)]">
        <ArrowLeft size={15} /> All Form 2290 reject codes
      </Link>

      <div className="flex flex-wrap items-center gap-2 mt-5">
        <Tag tone="default">{section.label}</Tag>
        <Tag tone="default">{rule.severity}</Tag>
        <Tag tone="default">{rule.category}</Tag>
      </div>
      <h1 className="text-[32px] leading-[1.15] mt-3 font-disp">
        IRS reject code {rule.code}: {rule.title}
      </h1>
      <p className="text-[var(--muted)] mt-3 text-[16px] leading-[1.6]">{rule.explanation}</p>

      <Panel padded className="mt-6 !bg-[var(--bg-soft)]">
        <div className="text-xs uppercase tracking-[.1em] text-[var(--muted)] font-semibold">The IRS rule, word for word</div>
        <p className="mt-2 leading-[1.6] font-mono text-[14px]">
          {rule.code}: {rule.irsText}
        </p>
      </Panel>

      <h2 className="font-disp text-[24px] mt-10">How to fix {rule.code}</h2>
      <ol className="mt-3 grid gap-2 pl-5 list-decimal leading-[1.6]">
        {rule.howToFix.map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
      <p className="mt-4 leading-[1.6]">
        If you filed with EasyFile2290, the rejection on your return links straight to the {section.step} step with this
        explanation, so you don't have to look the code up.
      </p>

      <Panel padded className="!bg-[var(--accent-soft)] flex flex-wrap items-center justify-between gap-5 mt-8">
        <div>
          <b className="font-disp text-[18px]">Fix it and file again</b>
          <p className="text-[var(--muted)] text-sm mt-1 max-w-[46ch]">Open the rejected return from your dashboard, correct it, and resubmit.</p>
        </div>
        <Link to="/dashboard">
          <Button>
            Open my returns <ArrowRight size={16} />
          </Button>
        </Link>
      </Panel>

      <h2 className="font-disp text-[22px] mt-10">Related guides</h2>
      <ul className="mt-3 grid gap-1.5 pl-5 list-disc">
        {section.guides.map((g) => (
          <li key={g.to}>
            <Link to={g.to} className="text-[var(--accent)]">{g.label}</Link>
          </li>
        ))}
        <li>
          <Link to="/blog/form-2290-rejected-common-reasons" className="text-[var(--accent)]">Form 2290 rejected? The most common reasons</Link>
        </li>
      </ul>

      {siblings.length > 0 && (
        <>
          <h2 className="font-disp text-[22px] mt-10">Other {section.label.toLowerCase()} reject codes</h2>
          <ul className="mt-3 grid gap-1.5 pl-5 list-disc">
            {siblings.map((s) => (
              <li key={s.code}>
                <Link to={`/form-2290-reject-codes/${s.codeSlug}`} className="text-[var(--accent)]">
                  {s.code}: {s.title}
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}

      <Panel padded className="flex flex-wrap items-center justify-between gap-5 mt-10">
        <div>
          <b className="font-disp text-[17px]">Filed somewhere else and got rejected?</b>
          <p className="text-[var(--muted)] text-sm mt-1 max-w-[50ch]">
            Start a fresh return here. Every return is checked against the IRS e-file schema and key IRS business
            rules before it is sent.
          </p>
        </div>
        <Link to="/login?tab=up">
          <Button>
            Create your free account <ArrowRight size={16} />
          </Button>
        </Link>
      </Panel>

      <p className="text-[var(--muted)] text-xs mt-11 pt-5 border-t border-[var(--line)] max-w-[70ch]">
        Rule text from the IRS Form 2290 Business Rules Publication (TY2026 v1.1). Explanation and fix steps are
        EasyFile2290's. General information, not tax advice for your situation.
      </p>
    </div>
  );
};

export default RejectCodePage;
